import React, { useState, useEffect, ReactNode, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./EditProgram.module.css";
import DraggableList from "../../common/components/draggableList/DraggableList";
import AddCircle from "@material-ui/icons/AddCircle";
import {
    Modal,
    TextField,
    Button,
    CircularProgress,
    createTheme,
    ThemeProvider,
    createStyles,
    makeStyles,
} from "@material-ui/core";
import { AppState } from "../../root/RootReducer";
import { GetAllProgram, UpdateProgram } from "../../stores/program/ProgramActions";
import { BackendList, ProgramDetail } from "../../stores/program/model/Program";
import PuiFloatButton from "../../components/elements/PuiFloatButton";
import PuiTypography from "../../components/elements/PuiTypography";
import PuiMessage from "../../components/widgets/PuiMessage";
import { isEmpty } from "../../utils/ObjectUtils";
import { setMessageArgs } from "../../utils/LogicUtils";
import { MSG_LOGIC_UPDATE_FAILED, MSG_LOGIC_UPDATE_FINISH } from "../../utils/Messages";

interface EditProgramProps {
    setIsSaved: (isSaved: boolean) => void;
    isSaved: boolean;
}

const useStyles = makeStyles(() =>
    createStyles({
        programTitle: {
            height: "110px",
            width: "100%",
            "& .MuiInputBase-root": {
                // color: "var(--loading-bg)",
                fontFamily: "noto-sans-cjk-jp",
                "& .MuiOutlinedInput-notchedOutline": {
                    // borderColor: "var(--loading-bg)",
                },
            },
        },
        programDetail: {
            height: "190px",
            width: "100%",
            marginBottom: "30px!important",
            border: "1px solid rgba(0, 0, 0, 0.23) !important",
            borderRadius: "4px !important",
            overflow: "scroll",
            msOverflowStyle: "none",
            scrollbarWidth: "none",
            "& .MuiInputBase-root": {
                color: "var(--loading-bg)",
                fontFamily: "noto-sans-cjk-jp",
                "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--loading-bg)",
                },
            },
        },
    })
);

const theme = createTheme({
    palette: {
        secondary: {
            main: "#12e07e",
        },
    },
});

export default function EditProgram({ setIsSaved, isSaved }: EditProgramProps) {
    const classes = useStyles();
    const { userInfo } = useSelector((state: AppState) => ({
        userInfo: state.user.userInfo,
    }));

    const dispatch = useDispatch();
    const inputEditImageRef = useRef<HTMLInputElement>(null);
    //APIコール
    useEffect(() => {
        if (userInfo) {
            dispatch(GetAllProgram(userInfo.id));
        }
    }, [dispatch, userInfo]);

    // バックエンドから取得したプログラム一覧リスト
    const { allProgramList, isLoading, isUpdateProgram, errorMessage } = useSelector(
        ({ program }: AppState) => program
    );
    // 更新・表示用のプログラムリスト
    const [programList, ChangeProgramList] = useState<ProgramDetail[]>([]);
    // 削除用のプログラムIDリスト
    const [deleteProgramIdList, ChangeDeleteProgramIdList] = useState<number[]>([]);
    // 更新用のプログラムIDリスト
    const [updateProgramIdList, ChangeUpdateProgramIdList] = useState<number[]>([]);
    // 詳細を表示するプログラムのindex
    const [visibleProgramIndex, ChangeVisibleProgramIndex] = useState(-1);
    // 詳細を表示するプログラムのid
    const [visibleProgramId, ChangeVisibleProgramId] = useState("-1");
    // 押下されたプログラムのタイトル
    const [programTitle, ShowProgramTitle] = useState("");
    // 押下されたプログラムの詳細
    const [programDetail, ShowProgramDetail] = useState("");
    // 押下されたプログラムのサムネイル表示切り替え 1:url 2:upload 3:uploadPreview
    const [visiblePreviewImage, ChangevisiblePreviewImage] = useState(1);
    // 押下されたプログラムのサムネイル画像ファイル
    const [previewImageFile, ChangePreviewImageFile] = useState(null);
    // 押下されたプログラムのサムネイル文言
    const [textProgramImageText, ChangeTextProgramImageText] =
        useState("Channelサムネイル用のファイルを選択");

    // メッセージ制御用
    const [showMessage, setShowMessage] = useState<boolean>(false);
    const [msgTitle, setMsgTitle] = useState<string>("");
    const [msgDetail, setMsgDetail] = useState<ReactNode[]>([]);
    const [msgDetailMod, setMsgDetailMod] = useState<string[]>([]);
    const [msgDetailAdd, setMsgDetailAdd] = useState<string[]>([]);
    const [msgDetailDel, setMsgDetailDel] = useState<string[]>([]);

    // メッセージの初期化
    useEffect(() => {
        if (showMessage) {
            // メッセージ詳細を生成する
            let detailText: ReactNode[] = [];
            if (msgDetailAdd.length > 0) {
                detailText.push(<PuiTypography bold={true}>{"追加Channel"}</PuiTypography>);
                msgDetailAdd.forEach((title: string) => {
                    detailText.push(<PuiTypography>{`　・${title}`}</PuiTypography>);
                });
                detailText.push(<p />);
            }

            if (msgDetailMod.length > 0) {
                detailText.push(<PuiTypography bold={true}>{"変更Channel"}</PuiTypography>);
                msgDetailMod.forEach((title: string) => {
                    detailText.push(<PuiTypography>{`　・${title}`}</PuiTypography>);
                });
                detailText.push(<p />);
            }

            if (msgDetailDel.length > 0) {
                detailText.push(<PuiTypography bold={true}>{"削除Channel"}</PuiTypography>);
                msgDetailDel.forEach((title: string) => {
                    detailText.push(<PuiTypography>{`　・${title}`}</PuiTypography>);
                });
                detailText.push(<p />);
            }

            setMsgDetail(detailText);

            if (isEmpty(errorMessage)) {
                setMsgTitle(setMessageArgs(MSG_LOGIC_UPDATE_FINISH, "Channel"));
            } else {
                setMsgTitle(setMessageArgs(MSG_LOGIC_UPDATE_FAILED, "Channel"));
            }
        }
    }, [showMessage, errorMessage, programList, msgDetailAdd, msgDetailMod, msgDetailDel]);

    // メッセージを閉じる
    const closeMsg = () => {
        setShowMessage(false);
    };

    const UpdateProgramList = (newProgramList: ProgramDetail[]) => {
        newProgramList !== undefined &&
            Object.keys(newProgramList).length !== 0 &&
            ChangeProgramList(newProgramList);
    };

    // 削除のIDリスト
    const DeleteProgramIdList = (deleteProgramId: number) => {
        if (!deleteProgramId) return;
        const newDeleteProgramIdList = [...deleteProgramIdList, deleteProgramId];
        ChangeDeleteProgramIdList(newDeleteProgramIdList);
    };

    // プログラムリストが更新される時、該当プログラムIDを保存しておく
    const UpdateProgramIdList = (updateProgramId: number) => {
        if (!updateProgramId || updateProgramIdList.includes(updateProgramId)) return;
        const newUpdateProgramIdList =
            updateProgramIdList.length !== 0 ? [...updateProgramIdList] : [];
        newUpdateProgramIdList.push(updateProgramId);
        ChangeUpdateProgramIdList(newUpdateProgramIdList);
    };

    // プログラムリスト項目押下時、押下されたプログラムの詳細を表示する
    const onClickProgram = (targetId: string) => {
        if (visibleProgramIndex !== -1) {
            if (
                programList[visibleProgramIndex].title !== "" &&
                programList[visibleProgramIndex].description !== "" &&
                isErrorTitleValidation === false &&
                isErrorDescriptionValidation === false
            ) {
                CheckInputAllFormItem(false);
                ChangeVisibleProgramIndex(
                    Number(programList.findIndex(({ id }) => id === targetId))
                );
            } else {
                CheckInputAllFormItem(true);
            }
        } else {
            ChangeVisibleProgramIndex(Number(programList.findIndex(({ id }) => id === targetId)));
        }
    };

    // ユーザのリストの削除、並び替え時にコールされる
    const onUpdate = (targetId: string, targetList: any, deleteId: number) => {
        const oldProgramList = programList;

        let pTitle = "";

        switch (targetId) {
            case "programList": {
                const newProgramList: ProgramDetail[] = targetList;
                // 現在表示しているプログラムが削除された場合、プログラムを非表示にする
                if (Object.keys(newProgramList).length !== Object.keys(oldProgramList).length) {
                    const includesVisibleIndex: string[] = [];
                    newProgramList.map((row: ProgramDetail) => includesVisibleIndex.push(row.id));
                    if (
                        visibleProgramIndex !== -1 &&
                        includesVisibleIndex.includes(oldProgramList[visibleProgramIndex].id) ===
                            false
                    ) {
                        ChangeVisibleProgramIndex(-1);
                        ShowProgramTitle("");
                        ShowProgramDetail("");
                        ChangevisiblePreviewImage(1);
                        ChangeTextProgramImageText("Channelサムネイル用のファイルを選択");
                        ChangePreviewImageFile(null);
                        if (inputEditImageRef.current) {
                            inputEditImageRef.current.value = "";
                        }
                    }
                    // 現在表示しているプログラムが削除されていない場合、visibleProgramIndexを更新する
                    else if (
                        visibleProgramIndex !== -1 &&
                        includesVisibleIndex.includes(oldProgramList[visibleProgramIndex].id) ===
                            true
                    ) {
                        ChangeVisibleProgramIndex(
                            Number(
                                newProgramList.findIndex(
                                    ({ id }) => id === oldProgramList[visibleProgramIndex].id
                                )
                            )
                        );
                    }
                }
                DeleteProgramIdList(deleteId);
                UpdateProgramList(newProgramList);
                setIsSaved(false);
                const diffList: ProgramDetail[] = programList.filter(
                    (p) => newProgramList.indexOf(p) === -1
                );
                if (diffList.length > 0) {
                    pTitle = diffList[0].title;
                }
                break;
            }

            case "episodeVideoList":
                programList[visibleProgramIndex].videos = targetList;
                setIsSaved(false);
                pTitle = programTitle;
                break;

            case "relatedVideoList":
                programList[visibleProgramIndex].relatedVideos = targetList;
                setIsSaved(false);
                pTitle = programTitle;
                break;

            default:
                break;
        }

        if (!isEmpty(pTitle)) {
            if (msgDetailAdd.includes(pTitle)) {
                setMsgDetailAdd(msgDetailAdd.filter((item) => item !== pTitle));
            }

            if (msgDetailMod.includes(pTitle)) {
                setMsgDetailMod(msgDetailMod.filter((item) => item !== pTitle));
            }

            if (!msgDetailDel.includes(pTitle)) {
                setMsgDetailDel([...msgDetailDel, pTitle]);
            }
        }
    };

    // ユーザのプログラムタイトルの編集
    const [isErrorTitleValidation, CheckTitleValidation] = useState(false);
    const ChangeProgramTitle = (event: any) => {
        if (CountWord(event.target.value) > 20) {
            CheckTitleValidation(true);
            programList[visibleProgramIndex].title = event.target.value;
            ShowProgramTitle(programList[visibleProgramIndex].title);
        } else {
            CheckTitleValidation(false);
            programList[visibleProgramIndex].title = event.target.value;
            ShowProgramTitle(programList[visibleProgramIndex].title);
        }
        setIsSaved(false);

        if (msgDetailAdd.includes(event.target.value)) {
            return;
        }

        if (!msgDetailMod.includes(event.target.value)) {
            setMsgDetailMod([...msgDetailMod, event.target.value]);
        }
    };

    // ユーザのプログラム詳細の編集
    const [isErrorDescriptionValidation, CheckDescriptionValidation] = useState(false);
    const ChangeProgramDetail = (event: any) => {
        if (CountWord(event.target.value) > 200) {
            CheckDescriptionValidation(true);
            programList[visibleProgramIndex].description = event.target.value;
            ShowProgramDetail(programList[visibleProgramIndex].description);
        } else {
            CheckDescriptionValidation(false);
            programList[visibleProgramIndex].description = event.target.value;
            ShowProgramDetail(programList[visibleProgramIndex].description);
        }
        setIsSaved(false);

        if (msgDetailAdd.includes(programTitle)) {
            return;
        }

        if (!msgDetailMod.includes(programTitle)) {
            setMsgDetailMod([...msgDetailMod, programTitle]);
        }
    };

    // ユーザのプログラムサムネイルの編集
    const [isErrorInputSize, ChangeErrorInputSize] = useState(false);
    // ファイルサイズエラーバー消去
    const CloseErrorInputBar = () => {
        ChangeErrorInputSize(false);
    };

    // ユーザのプログラムサムネイルの表示切り替え
    const ChangeProgramImage = () => {
        programList[visibleProgramIndex].imageUrl = "";
        programList[visibleProgramIndex].showImageUrl = "";
        programList[visibleProgramIndex].file = null;
        ChangevisiblePreviewImage(2);
        ChangeTextProgramImageText("Channelサムネイル用のファイルを選択");
        ChangePreviewImageFile(null);
        if (inputEditImageRef.current) {
            inputEditImageRef.current.value = "";
        }
        setIsSaved(false);

        if (msgDetailAdd.includes(programTitle)) {
            return;
        }

        if (!msgDetailMod.includes(programTitle)) {
            setMsgDetailMod([...msgDetailMod, programTitle]);
        }
    };

    const ChangeProgramImageFile = (event: any) => {
        if (event.target.files.length === 0) {
            ChangevisiblePreviewImage(2);
            ChangeTextProgramImageText("Channelサムネイル用のファイルを選択");
            ChangePreviewImageFile(null);
            if (inputEditImageRef.current) {
                inputEditImageRef.current.value = "";
            }
        } else if (event.target.files.length === 1) {
            if (event.target.files[0].size > 0) {
                ChangeTextProgramImageText(event.target.files[0].name);
                ChangePreviewImageFile(event.target.files[0]);
                programList[visibleProgramIndex].file = event.target.files[0];
                ChangevisiblePreviewImage(3);
            } else {
                ChangeErrorInputSize(true);
                if (inputEditImageRef.current) {
                    inputEditImageRef.current.value = "";
                }
            }
        } else {
            ChangevisiblePreviewImage(2);
            ChangeTextProgramImageText("ファイルを正しく選択してください");
            ChangePreviewImageFile(null);
            if (inputEditImageRef.current) {
                inputEditImageRef.current.value = "";
            }
        }
        setIsSaved(false);

        if (msgDetailAdd.includes(programTitle)) {
            return;
        }

        if (!msgDetailMod.includes(programTitle)) {
            setMsgDetailMod([...msgDetailMod, programTitle]);
        }
    };

    // （新規チャンネル || チャンネル更新）の場合、trueを返す
    const IsChangeUpdateList = (id: string) => {
        if (updateProgramIdList.includes(Number(id))) {
            return true;
        }
        return false;
    };

    // 更新ボタン押下で入力チェック
    const [isInputAllFormItem, CheckInputAllFormItem] = useState(false);
    // ユーザの更新ボタン押下
    const updateList: BackendList[] = [];
    // const [isUpdateFlag, ChangeUpdateFlag] = useState(false);
    const ClickUpdate = () => {
        // 現在の項目が入力されているかチェック
        if (visibleProgramIndex !== -1) {
            if (
                programList[visibleProgramIndex].title !== "" &&
                programList[visibleProgramIndex].description !== "" &&
                isErrorTitleValidation === false &&
                isErrorDescriptionValidation === false
            ) {
                programList.map(function (row) {
                    if (Object.keys(updateList).length < Object.keys(programList).length) {
                        const videoIdList: string[] = [];
                        row.videos !== undefined &&
                            row.videos.map((item) => videoIdList.push(item.id));
                        const relatedVideoIdList: string[] = [];
                        row.relatedVideos !== undefined &&
                            row.relatedVideos.map((item) => relatedVideoIdList.push(item.id));

                        // リストの変更があるかどうか
                        const updateFlag = IsChangeUpdateList(row.id);
                        updateList.push({
                            id: row.id,
                            title: row.title,
                            description: row.description,
                            imageUrl: row.imageUrl,
                            file: row.file,
                            videoIds: videoIdList,
                            relatedVideoIds: relatedVideoIdList,
                            updateFlag: updateFlag,
                        });
                        // ChangeUpdateFlag(true);
                        if (
                            Object.keys(updateList).length >= 1 &&
                            Object.keys(updateList).length === Object.keys(programList).length
                        ) {
                            UpdateProgramList([]);
                            if (userInfo)
                                dispatch(
                                    UpdateProgram(userInfo.id, updateList, deleteProgramIdList)
                                );

                            // 初期化
                            ResetInput();
                            setIsSaved(true);
                            setShowMessage(true);
                        }
                    }
                    return null;
                });
            } else {
                CheckInputAllFormItem(true);
            }
        } else {
            // プログラムを全件削除する場合 || 新規追加の場合
            if (programList.length !== 0) {
                programList.map(function (row) {
                    if (Object.keys(updateList).length < Object.keys(programList).length) {
                        const videoIdList: string[] = [];
                        row.videos !== undefined &&
                            row.videos.map((item) => videoIdList.push(item.id));
                        const relatedVideoIdList: string[] = [];
                        row.relatedVideos !== undefined &&
                            row.relatedVideos.map((item) => relatedVideoIdList.push(item.id));

                        // リストの変更があるかどうか
                        const updateFlag = IsChangeUpdateList(row.id);
                        updateList.push({
                            id: row.id,
                            title: row.title,
                            description: row.description,
                            imageUrl: row.imageUrl,
                            file: row.file,
                            videoIds: videoIdList,
                            relatedVideoIds: relatedVideoIdList,
                            updateFlag: updateFlag,
                        });
                        // ChangeUpdateFlag(true);
                        if (
                            Object.keys(updateList).length >= 1 &&
                            Object.keys(updateList).length === Object.keys(programList).length
                        ) {
                            UpdateProgramList([]);
                            if (userInfo)
                                dispatch(
                                    UpdateProgram(userInfo.id, updateList, deleteProgramIdList)
                                );
                            // 初期化
                            ResetInput();
                            setIsSaved(true);
                            setShowMessage(true);
                        }
                    }
                    return null;
                });
            } else {
                // 全件削除の場合
                if (isSaved === false) {
                    // ChangeUpdateFlag(true);
                    // updateList.push({id: "", title: "", description: "", imageUrl: "", file: null, videoIds: [], relatedVideoIds: []});
                    if (userInfo) dispatch(UpdateProgram(userInfo.id, [], deleteProgramIdList));
                    // if(userInfo) setTimeout(() => dispatch(GetAllProgram(userInfo.id)), 1000);
                    ResetInput();
                    setIsSaved(true);
                    setShowMessage(true);
                }
            }
        }
    };

    const ResetInput = () => {
        ChangeVisibleProgramIndex(-1);
        ShowProgramTitle("");
        ShowProgramDetail("");
        ChangevisiblePreviewImage(1);
        ChangePreviewImageFile(null);
        if (inputEditImageRef.current) {
            inputEditImageRef.current.value = "";
        }
        ChangeTextProgramImageText("Channelサムネイル用のファイルを選択");
        CheckInputAllFormItem(false);
    };

    useEffect(() => {
        if (Object.keys(allProgramList).length > 0) {
            ChangeProgramList(allProgramList);
        }
    }, [allProgramList]);

    //  インデックス更新
    useEffect(() => {
        if (visibleProgramIndex !== -1) {
            ChangeVisibleProgramId(programList[visibleProgramIndex].id);
            UpdateProgramIdList(Number(programList[visibleProgramIndex].id));
        } else {
            ChangeVisibleProgramId("-1");
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleProgramIndex]);

    // 押下により表示するプログラムタイトル、プログラム詳細切り替え
    useEffect(() => {
        if (visibleProgramIndex !== -1) {
            ShowProgramTitle(programList[visibleProgramIndex].title);
            ShowProgramDetail(programList[visibleProgramIndex].description);
            if (
                programList[visibleProgramIndex].file !== undefined &&
                programList[visibleProgramIndex].file !== null
            ) {
                ChangePreviewImageFile(programList[visibleProgramIndex].file);
                ChangevisiblePreviewImage(3);
            } else if (
                programList[visibleProgramIndex].imageUrl !== undefined &&
                programList[visibleProgramIndex].imageUrl !== ""
            ) {
                ChangevisiblePreviewImage(1);
            } else {
                ChangevisiblePreviewImage(2);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleProgramIndex]);

    // 更新ボタンの有無効制御
    const [isReadyToUpdate, ChangeReadyToSendFlag] = useState(false);
    useEffect(() => {
        if (!isSaved && !isErrorTitleValidation && !isErrorDescriptionValidation) {
            ChangeReadyToSendFlag(false);
        } else {
            ChangeReadyToSendFlag(true);
        }
    }, [isSaved, isErrorTitleValidation, isErrorDescriptionValidation]);

    // -------------------- プログラムの新規追加 -------------------- //

    const inputNewImageRef = useRef<HTMLInputElement>(null);

    // プログラム追加モーダル画面表示制御
    const [isOpenAddProgramModal, ShowAddProgramModal] = React.useState(false);
    const OpenAddProgramModal = () => {
        ShowAddProgramModal(true);
    };
    const CloseAddProgramModal = () => {
        if (!msgDetailAdd.includes(inputNewProgramTitle)) {
            setMsgDetailAdd([...msgDetailAdd, inputNewProgramTitle]);
        }

        ShowAddProgramModal(false);
        InputNewProgramTitle("");
        InputNewProgramDetail("");
        ChangevisibleNewPreviewImage(false);
        ChangeUploadNewImageMessage("Channelサムネイル用のファイルを選択");
        InputNewImageFile(null);
        if (inputNewImageRef.current) {
            inputNewImageRef.current.value = "";
        }
        CheckInputAllFormNewItem(false);
    };

    // ユーザ入力プログラムタイトル
    const [inputNewProgramTitle, InputNewProgramTitle] = useState("");
    // ユーザ入力プログラム詳細
    const [inputNewProgramDetail, InputNewProgramDetail] = useState("");
    // プログラムのサムネイル表示切り替え
    const [visibleNewPreviewImage, ChangevisibleNewPreviewImage] = useState(false);
    // プログラムのサムネイル表示文言
    const [uploadNewImageMessage, ChangeUploadNewImageMessage] = useState(
        "Channelサムネイル用のファイルを選択(任意)"
    );
    // プログラムのサムネイル画像ファイル
    const [inputNewImageFile, InputNewImageFile] = useState(null);

    const [isErrorNewInputSize, ChangeErrorNewInputSize] = useState(false);
    // ファイルサイズエラーバー消去
    const CloseErrorNewInputBar = () => {
        ChangeErrorNewInputSize(false);
    };

    // ユーザの追加プログラムタイトル入力
    const [isErrorNewTitleValidation, CheckNewTitleValidation] = useState(false);
    const ChangeNewProgramTitle = (event: any) => {
        if (CountWord(event.target.value) > 20) {
            CheckNewTitleValidation(true);
        } else {
            CheckNewTitleValidation(false);
            InputNewProgramTitle(event.target.value);
        }
    };
    // ユーザの追加プログラム詳細入力
    const [isErrorNewDescriptionValidation, CheckNewDescriptionValidation] = useState(false);
    const ChangeNewProgramDetail = (event: any) => {
        if (CountWord(event.target.value) > 200) {
            CheckNewDescriptionValidation(true);
        } else {
            CheckNewDescriptionValidation(false);
            InputNewProgramDetail(event.target.value);
        }
    };

    // ユーザの追加プログラムサムネイルアップロード
    const UserUploadImage = (event: any) => {
        if (event.target.files.length === 0) {
            ChangeUploadNewImageMessage("Channelサムネイル用のファイルを選択");
            InputNewImageFile(null);
            if (inputNewImageRef.current) {
                inputNewImageRef.current.value = "";
            }
            ChangevisibleNewPreviewImage(false);
        } else if (event.target.files.length === 1) {
            if (event.target.files[0].size > 0) {
                ChangeUploadNewImageMessage(event.target.files[0].name);
                InputNewImageFile(event.target.files[0]);
                ChangevisibleNewPreviewImage(true);
            } else {
                ChangeErrorNewInputSize(true);
                if (inputNewImageRef.current) {
                    inputNewImageRef.current.value = "";
                }
            }
        } else {
            ChangeUploadNewImageMessage("ファイルを正しく選択してください");
            InputNewImageFile(null);
            if (inputNewImageRef.current) {
                inputNewImageRef.current.value = "";
            }
            ChangevisibleNewPreviewImage(false);
        }
    };

    // プレビュー表示中に画像押下
    const ChangeAddProgramImage = () => {
        ChangevisibleNewPreviewImage(false);
        ChangeUploadNewImageMessage("Channelサムネイル用のファイルを選択");
        InputNewImageFile(null);
        if (inputNewImageRef.current) {
            inputNewImageRef.current.value = "";
        }
    };

    // 追加ボタン押下で入力チェック
    const [isInputAllFormNewItem, CheckInputAllFormNewItem] = useState(false);

    // ユーザのプログラム追加ボタン押下
    const ClickAddProgram = () => {
        if (
            inputNewProgramTitle !== "" &&
            inputNewProgramDetail !== "" &&
            isErrorNewTitleValidation !== true &&
            isErrorNewDescriptionValidation !== true
        ) {
            CheckInputAllFormNewItem(false);
            CloseAddProgramModal();
            AddProgram();
        } else {
            CheckInputAllFormNewItem(true);
        }
    };

    // プログラム追加処理
    const AddProgram = () => {
        const AddProgramId = Date.now();
        programList.push({
            id: String(AddProgramId),
            title: inputNewProgramTitle,
            description: inputNewProgramDetail,
            showImageUrl: "",
            imageUrl: "",
            file: inputNewImageFile,
            videos: [],
            relatedVideos: [],
            updateFlag: true,
        });
        UpdateProgramIdList(AddProgramId);
        InputNewProgramTitle("");
        InputNewProgramDetail("");
        ChangevisibleNewPreviewImage(false);
        ChangeUploadNewImageMessage("Channelサムネイル用のファイルを選択");
        InputNewImageFile(null);
        if (inputNewImageRef.current) {
            inputNewImageRef.current.value = "";
        }
        setIsSaved(false);
    };

    // 文字数カウント
    const CountWord = (word: string): number => {
        let count = 0;
        for (let i = 0; i < word.length; i++) {
            if (word[i].match(/[ -~]/)) {
                count += 0.5;
            } else {
                count += 1;
            }
        }
        return count;
    };

    // 表示待機
    if (isLoading && isUpdateProgram) {
        return (
            <div className={styles.noContent}>
                <CircularProgress size={60} style={{ color: "var(--loading-bg)" }} />
            </div>
        );
    }

    return (
        <div className={styles.root}>
            {/* プログラムリスト */}
            <div className={styles.left}>
                {programList !== undefined && (
                    <div>
                        <div className={styles.listTitle}>
                            <PuiTypography size={24} bold={true} color={"black"}>
                                Channel一覧
                            </PuiTypography>
                        </div>
                        <div className={styles.programList}>
                            <DraggableList
                                type="programList"
                                currentId={visibleProgramId}
                                targetList={programList}
                                onClick={onClickProgram}
                                onUpdate={onUpdate}
                                showListNum={8}
                            />
                            <div style={{ textAlign: "center" }}>
                                <AddCircle
                                    className={styles.addButton}
                                    style={{
                                        height: "40px",
                                        width: "40px",
                                        cursor: "pointer",
                                        color: "var(--main)",
                                        textAlign: "center",
                                    }}
                                    onClick={OpenAddProgramModal}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {/* 押下されたプログラムタイトル   */}
            <div className={styles.middle}>
                {programList !== undefined &&
                    programList.length !== 0 &&
                    visibleProgramIndex !== -1 && (
                        <div>
                            <div className={styles.listTitle}>
                                <PuiTypography size={24} bold={true} color={"black"}>
                                    Channel名
                                </PuiTypography>
                            </div>
                            <TextField
                                color="secondary"
                                id="VideoName"
                                variant="outlined"
                                className={classes.programTitle}
                                multiline
                                maxRows={2}
                                autoComplete="off"
                                error={isErrorTitleValidation}
                                helperText={
                                    isErrorTitleValidation &&
                                    "全角20文字（半角40文字）以内で正しく入力してください"
                                }
                                value={programTitle}
                                onChange={ChangeProgramTitle}
                            />
                        </div>
                    )}

                {/* 押下されたプログラム詳細 */}
                {programList !== undefined &&
                    programList.length !== 0 &&
                    visibleProgramIndex !== -1 && (
                        <div>
                            <div className={styles.listTitle}>
                                <PuiTypography size={24} bold={true} color={"black"}>
                                    Channel詳細
                                </PuiTypography>
                            </div>
                            <TextField
                                color="secondary"
                                id="description"
                                className={styles.programDetail}
                                multiline
                                minRows={6}
                                autoComplete="off"
                                error={isErrorDescriptionValidation}
                                helperText={
                                    isErrorDescriptionValidation &&
                                    "全角200文字（半角400文字）以内で正しく入力してください"
                                }
                                variant="outlined"
                                value={programDetail}
                                onChange={ChangeProgramDetail}
                            />
                        </div>
                    )}
                {/* 押下されたプログラムサムネイル */}
                {programList !== undefined &&
                    programList.length !== 0 &&
                    visibleProgramIndex !== -1 &&
                    programList[visibleProgramIndex] && (
                        <div>
                            <div className={styles.listTitle}>
                                <PuiTypography size={24} bold={true} color={"black"}>
                                    サムネイル画像
                                </PuiTypography>
                            </div>
                            {programList[visibleProgramIndex].showImageUrl &&
                                visiblePreviewImage === 1 &&
                                visibleProgramIndex !== -1 && (
                                    <div className={styles.previewImage}>
                                        <img
                                            alt="defaultThumbnail"
                                            src={programList[visibleProgramIndex].imageUrl}
                                            onClick={ChangeProgramImage}
                                        />
                                    </div>
                                )}
                            {visiblePreviewImage === 2 && (
                                <div className={styles.form}>
                                    <label className={styles.uploadButton}>
                                        <input
                                            id="editProgramImage"
                                            ref={inputEditImageRef}
                                            multiple
                                            type="file"
                                            accept="image/*"
                                            onChange={ChangeProgramImageFile}
                                            className={styles.fileDisplay}
                                        />
                                        {textProgramImageText}
                                    </label>
                                    <p className={styles.previewText}>
                                        指定しない場合は、自動的にサムネイルが設定されます
                                    </p>
                                </div>
                            )}
                            {visiblePreviewImage === 3 && previewImageFile && (
                                <div className={styles.previewImage}>
                                    <img
                                        alt="thumbnail"
                                        src={window.URL.createObjectURL(previewImageFile)}
                                        onClick={ChangeProgramImage}
                                    />
                                </div>
                            )}
                        </div>
                    )}
            </div>

            <div className={styles.right}>
                {/* エピソードビデオリスト */}
                <div
                    style={{
                        visibility:
                            programList !== undefined &&
                            programList.length !== 0 &&
                            visibleProgramIndex !== -1
                                ? "visible"
                                : "hidden",
                    }}
                >
                    <h2 className={styles.listTitle}>エピソード一覧</h2>
                    <div className={styles.episodeList}>
                        {programList !== undefined &&
                            programList.length !== 0 &&
                            visibleProgramIndex !== -1 &&
                            programList[visibleProgramIndex] &&
                            programList[visibleProgramIndex].videos && (
                                <DraggableList
                                    type="episodeVideoList"
                                    onUpdate={onUpdate}
                                    targetList={
                                        programList[visibleProgramIndex].videos &&
                                        programList[visibleProgramIndex].videos
                                    }
                                    listHeight={200}
                                    showListNum={3}
                                />
                            )}
                    </div>
                </div>

                {/* 関連ビデオリスト */}
                <div
                    style={{
                        visibility:
                            programList !== undefined &&
                            programList.length !== 0 &&
                            visibleProgramIndex !== -1
                                ? "visible"
                                : "hidden",
                    }}
                >
                    <h2 className={styles.listTitle}>関連Video一覧</h2>
                    <div className={styles.relatedList}>
                        {programList !== undefined &&
                            programList.length !== 0 &&
                            visibleProgramIndex !== -1 &&
                            programList[visibleProgramIndex] &&
                            programList[visibleProgramIndex].relatedVideos && (
                                <DraggableList
                                    type="relatedVideoList"
                                    targetList={
                                        programList[visibleProgramIndex].relatedVideos &&
                                        programList[visibleProgramIndex].relatedVideos
                                    }
                                    onUpdate={onUpdate}
                                    listHeight={200}
                                    showListNum={3}
                                />
                            )}
                    </div>
                </div>

                {/* 更新ボタン */}
                {/* {programList !== undefined && (
                    <div style={{ display: "inline-block" }} className={styles.button}>
                        <Button
                            size="small"
                            variant="outlined"
                            type="submit"
                            color="secondary"
                            className={styles.updateButton}
                            onClick={ClickUpdate}
                            disabled={isReadyToUpdate}
                        >
                            更新
                        </Button>
                        {isInputAllFormItem && (
                            <div className={styles.checkInput}>
                                ※全ての項目を正しく入力してください
                            </div>
                        )}
                    </div>
                )} */}
            </div>
            {programList !== undefined && (
                <div>
                    <div className={styles.updateButton}>
                        <PuiFloatButton
                            children={["　更新　"]}
                            size="medium"
                            isActive={!isReadyToUpdate}
                            onClick={ClickUpdate}
                        />
                    </div>
                    {isInputAllFormItem && (
                        <div className={styles.checkInput}>※全ての項目を正しく入力してください</div>
                    )}
                </div>
            )}

            {/* ----------------------------- プログラム追加モーダル ----------------------------- */}
            <Modal open={isOpenAddProgramModal} onClose={CloseAddProgramModal}>
                <div
                    style={{
                        backgroundColor: "white",
                        width: "600px",
                        height: "calc(100vh - 100px)",
                        margin: "10vh auto auto auto",
                    }}
                >
                    <ul className={styles.ul}>
                        <ThemeProvider theme={theme}>
                            <li className={styles.modalForm}>
                                <TextField
                                    color="secondary"
                                    id="ProgramTitle"
                                    variant="outlined"
                                    autoComplete="off"
                                    placeholder="Channelタイトルを入力してください"
                                    onChange={ChangeNewProgramTitle}
                                    style={{ width: "100%" }}
                                    error={isErrorNewTitleValidation}
                                    helperText={
                                        isErrorNewTitleValidation &&
                                        "全角20文字（半角40文字）以内で正しく入力してください"
                                    }
                                />
                            </li>

                            <li className={styles.modalForm}>
                                <TextField
                                    color="secondary"
                                    id="description"
                                    onChange={ChangeNewProgramDetail}
                                    multiline
                                    minRows={4}
                                    autoComplete="off"
                                    style={{ width: "100%" }}
                                    variant="outlined"
                                    placeholder="Channelの詳細を入力してください"
                                    error={isErrorNewDescriptionValidation}
                                    helperText={
                                        isErrorNewDescriptionValidation &&
                                        "全角200文字（半角400文字）以内で正しく入力してください"
                                    }
                                />
                            </li>
                        </ThemeProvider>

                        <li>
                            {visibleNewPreviewImage ? (
                                inputNewImageFile !== null && (
                                    <div className={styles.newPreviewImage}>
                                        <img
                                            alt="thumbnail"
                                            src={window.URL.createObjectURL(inputNewImageFile)}
                                            onClick={ChangeAddProgramImage}
                                        />
                                    </div>
                                )
                            ) : (
                                <label className={styles.newUploadButton}>
                                    <input
                                        id="addProgramImage"
                                        ref={inputNewImageRef}
                                        multiple
                                        type="file"
                                        accept="image/*"
                                        onChange={UserUploadImage}
                                        className={styles.fileDisplay}
                                    />
                                    {uploadNewImageMessage}
                                </label>
                            )}
                        </li>

                        <div className={styles.modalFormButton}>
                            {isInputAllFormNewItem && (
                                <div className={styles.checkNewInput}>
                                    ※全ての項目を正しく入力してください
                                </div>
                            )}
                            <Button
                                variant="outlined"
                                type="submit"
                                color="secondary"
                                onClick={ClickAddProgram}
                                style={{ borderColor: "var(--main)", color: "var(--main)" }}
                            >
                                Channel追加
                            </Button>
                        </div>
                    </ul>
                    <PuiMessage
                        open={isErrorNewInputSize}
                        type="error"
                        title="正しいファイルを選択してください"
                        duration={5000}
                        onClose={CloseErrorNewInputBar}
                    />
                </div>
            </Modal>

            <PuiMessage
                open={showMessage}
                type={isEmpty(errorMessage) ? "success" : "error"}
                title={msgTitle}
                detail={msgDetail}
                onClose={closeMsg}
            />

            <PuiMessage
                open={isErrorInputSize}
                type="error"
                title="正しいファイルを選択してください"
                duration={5000}
                onClose={CloseErrorInputBar}
            />
        </div>
    );
}
